import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  ReactNode,
} from 'react'
import {
  InteractionTrackingProvider,
  type InteractionEvent,
} from '@labrav/react-components'
import { useTracker } from '../utils/Tracker/hook'
import type { Tracker } from '../utils/Tracker/Tracker'

/** Keys whose values are a controlled vocabulary, so they must be stable and snake_case. */
const VOCABULARY_KEYS = [
  'product',
  'section',
  'component',
  'action',
  'route_key',
  /**
   * The specific workflow inside the product, when a product has several that
   * are worth telling apart — the funding program key (`mpopp`) is the case
   * this exists for. Leave it unset where the product has only one flow.
   */
  'workflow_key',
  'user_role_key',
  'interruption_reason',
] as const

/** Keys whose values are opaque backend identifiers — UUIDs, so no shape can be enforced. */
const IDENTIFIER_KEYS = [
  'partner_id',
  'partner_user_id',
  'account_id',
  'conversation_id',
  'turn_id',
  'funding_request_id',
] as const

const EVENT_KEYS = [...VOCABULARY_KEYS, ...IDENTIFIER_KEYS, 'filename'] as const

type EventKey = (typeof EVENT_KEYS)[number]

export type EventScopeMetadata = Partial<Record<EventKey, string>>

export type TrackedEvent = EventScopeMetadata & { name?: string }

const STABLE_KEY = /^[a-z0-9]+(_[a-z0-9]+)*$/

const EventScopeContext = createContext<EventScopeMetadata>({})

/**
 * Validate and deliver one occurrence against a committed scope. Shared by the
 * hook and by the primitive bridge, which need identical rules but read their
 * scope from different places.
 */
const sendEvent = (
  tracker: Tracker | null,
  metadata: EventScopeMetadata,
  event: TrackedEvent
) => {
  const { name, ...properties } = event
  const payload: Record<string, string> = { ...metadata }

  for (const [key, value] of Object.entries(properties)) {
    // Same posture as scope merging: an absent value inherits, it never
    // erases and never fails validation.
    if (value === undefined) continue

    const rejection = !(EVENT_KEYS as readonly string[]).includes(key)
      ? `key "${key}" is not allowlisted`
      : typeof value !== 'string'
        ? `key "${key}" must be a string`
        : (VOCABULARY_KEYS as readonly string[]).includes(key) &&
            !STABLE_KEY.test(value)
          ? `key "${key}" must be snake_case, got "${value}"`
          : undefined

    if (rejection) {
      if (import.meta.env.DEV) throw new Error(`[telemetry] ${rejection}`)
      return
    }
    payload[key] = value as string
  }

  tracker?.track({
    ...payload,
    ...(name ? { eventName: name } : {}),
    event_id: crypto.randomUUID(),
    occurred_at: new Date().toISOString(),
  })
}

export const EventScopeProvider = ({
  metadata,
  children,
}: {
  metadata: EventScopeMetadata
  children: ReactNode
}) => {
  const parent = useContext(EventScopeContext)
  const tracker = useTracker()

  // Keyed on the values, not the object, so callers can pass an inline literal
  // without re-rendering every consumer below them on each render.
  const metadataKey = JSON.stringify(metadata)

  const merged = useMemo(() => {
    const next: EventScopeMetadata = { ...parent }
    Object.entries(metadata).forEach(([key, value]) => {
      if (value !== undefined) next[key as EventKey] = value
    })
    return Object.freeze(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parent, metadataKey])

  // Republished at every scope, so a `trackAs` primitive reports with the page
  // it is actually on. A single bridge at the root would hand every button the
  // root's metadata — a provider cannot read the scope it is providing, so this
  // has to be built from `merged` here rather than from the context below.
  const onInteraction = useCallback(
    ({ component, action }: InteractionEvent) =>
      sendEvent(tracker, merged, { component, action }),
    [tracker, merged]
  )

  return (
    <EventScopeContext.Provider value={merged}>
      <InteractionTrackingProvider onInteraction={onInteraction}>
        {children}
      </InteractionTrackingProvider>
    </EventScopeContext.Provider>
  )
}

export const useTrackEvent = () => {
  const tracker = useTracker()
  const metadata = useContext(EventScopeContext)

  return useCallback(
    (event: TrackedEvent) => sendEvent(tracker, metadata, event),
    [tracker, metadata]
  )
}
